import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  ClickAwayListener,
  CircularProgress,
  List,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { autocompleteListings } from '../services/ListingService';
import { SEARCH_CONFIG } from '../config';

const normalizeInputValue = (value) => {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    if (typeof value.title === 'string') {
      return value.title;
    }
    return '';
  }
  return value == null ? '' : String(value);
};
/**
 * SearchAutocomplete component for search input with autocomplete suggestions
 * @param {Object} props - Component props
 * @param {string} props.value - Current search value
 * @param {Function} props.onChange - Callback when value changes
 * @param {Function} props.onSelect - Callback when suggestion is selected (receives suggestion object)
 * @param {Function} props.onEnter - Callback when Enter key is pressed (receives current input value)
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.sx - Additional sx styles
 * @param {Function} props.onBlur - Callback when input loses focus
 */
const SearchAutocomplete = forwardRef(function SearchAutocomplete({ value, onChange, onSelect, onEnter, placeholder = "Wyszukaj", sx = {}, onBlur }, ref) {
  const [inputValue, setInputValue] = useState(normalizeInputValue(value));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasResolvedQuery, setHasResolvedQuery] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    close: () => {
      setIsInputFocused(false);
      setHighlightedIndex(-1);
    },
    blur: () => {
      inputRef.current?.blur();
      setIsInputFocused(false);
      setHighlightedIndex(-1);
    },
    cancelRequests: () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }));

  const MIN_QUERY_LENGTH = SEARCH_CONFIG.AUTOCOMPLETE_MIN_QUERY_LENGTH;
  const DEBOUNCE_DELAY = SEARCH_CONFIG.AUTOCOMPLETE_DEBOUNCE_DELAY;
  const hasEnoughCharacters = inputValue.trim().length >= MIN_QUERY_LENGTH;
  const shouldShowDropdown = isInputFocused && hasEnoughCharacters;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const normalizedValue = normalizeInputValue(value);
    if (value !== undefined && normalizedValue !== inputValue) {
      setInputValue(normalizedValue);
    }
  }, [inputValue, value]);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      if (isMountedRef.current) {
        setOptions([]);
        setHasResolvedQuery(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const nextRequestId = requestIdRef.current + 1;
    requestIdRef.current = nextRequestId;
    abortControllerRef.current = new AbortController();
    if (isMountedRef.current) {
      setLoading(true);
      setHasResolvedQuery(false);
    }

    try {
      const result = await autocompleteListings(query, {
        signal: abortControllerRef.current.signal
      });

      if (!isMountedRef.current || requestIdRef.current !== nextRequestId) {
        return;
      }

      const { data: response, meta } = result || {};
      if (meta) {
        console.warn('Autocomplete warning:', meta);
      }

      const normalizedOptions = Array.isArray(response) ? response : [];
      setOptions(normalizedOptions);
      setHasResolvedQuery(true);
      setHighlightedIndex(normalizedOptions.length > 0 ? 0 : -1);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      if (isMountedRef.current && requestIdRef.current === nextRequestId) {
        console.error('Error fetching autocomplete suggestions:', error);
        setOptions([]);
        setHasResolvedQuery(true);
        setHighlightedIndex(-1);
      }
    } finally {
      if (isMountedRef.current && requestIdRef.current === nextRequestId) {
        setLoading(false);
      }
    }
  }, [MIN_QUERY_LENGTH]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!hasEnoughCharacters) {
      setOptions([]);
      setLoading(false);
      setHasResolvedQuery(false);
      setHighlightedIndex(-1);
      return;
    }

    setLoading(true);
    setHasResolvedQuery(false);
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(inputValue.trim());
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [DEBOUNCE_DELAY, fetchSuggestions, hasEnoughCharacters, inputValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const closeDropdown = useCallback((event) => {
    setIsInputFocused(false);
    setHighlightedIndex(-1);
    if (onBlur) {
      onBlur(event);
    }
  }, [onBlur]);

  const selectSuggestion = useCallback((suggestion) => {
    if (!suggestion) {
      return;
    }
    setInputValue(suggestion.title || '');
    setIsInputFocused(false);
    setHighlightedIndex(-1);
    if (onSelect) {
      onSelect(suggestion);
    }
  }, [onSelect]);

  const handleInputChange = (event) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);
    setIsInputFocused(true);
    setHighlightedIndex(-1);
    if (newInputValue.trim().length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setHasResolvedQuery(false);
      setLoading(false);
    }
    if (onChange) {
      onChange(event, newInputValue);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      if (!shouldShowDropdown || options.length === 0) {
        return;
      }
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % options.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      if (!shouldShowDropdown || options.length === 0) {
        return;
      }
      event.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsInputFocused(false);
      setHighlightedIndex(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (shouldShowDropdown && highlightedIndex >= 0 && options[highlightedIndex]) {
        selectSuggestion(options[highlightedIndex]);
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      setIsInputFocused(false);
      setHighlightedIndex(-1);
      if (onEnter && inputValue.trim().length > 0) {
        onEnter(inputValue.trim());
      }
    }
  };

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <Box ref={rootRef} sx={{ position: 'relative', width: '100%', ...sx }}>
        <TextField
          inputRef={inputRef}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          fullWidth
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsInputFocused(true);
          }}
          onBlur={(event) => {
            setTimeout(() => {
              if (!rootRef.current?.contains(document.activeElement)) {
                closeDropdown(event);
              }
            }, 0);
          }}
          sx={{
            width: "100%",
          }}
          slotProps={{
            input: {
              startAdornment: (
                <>
                  <IconSearch style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} thickness={5} sx={{ mr: 1 }} /> : null}
                </>
              ),
            },
          }}
        />

        <Popper
          open={shouldShowDropdown}
          anchorEl={rootRef.current}
          placement="bottom-start"
          disablePortal
          sx={{
            zIndex: (theme) => theme.zIndex.modal,
            width: rootRef.current?.clientWidth || undefined,
          }}
        >
          <Paper elevation={4} sx={{ mt: 1, overflow: 'hidden' }}>
            {loading || !hasResolvedQuery ? (
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={18} thickness={5} />
                <Typography variant="body2" color="text.secondary">
                  Ladowanie podpowiedzi...
                </Typography>
              </Box>
            ) : options.length === 0 ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Brak wynikow
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0, maxHeight: 320, overflowY: 'auto' }}>
                {options.map((option, index) => (
                  <ListItemButton
                    key={`${option.title}-${option.categoryId || 'history'}-${option.fromHistory ? 'history' : 'db'}-${index}`}
                    selected={index === highlightedIndex}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => {
                      selectSuggestion(option);
                    }}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        {option.fromHistory && (
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
                          >
                            Historia
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                          {option.title}
                        </Typography>
                        {option.categoryName && (
                          <Typography variant="body2" color="text.secondary">
                            {option.categoryName}
                          </Typography>
                        )}
                      </Box>
                      {option.count !== undefined && option.count !== null && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {option.count} {option.count === 1 ? 'ogloszenie' : option.count < 5 ? 'ogloszenia' : 'ogloszen'}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
});

export default SearchAutocomplete;

